import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Col, Input, List, Popconfirm, Row, notification } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  ProfileOutlined,
  QuestionCircleOutlined,
  CheckOutlined,
  StopOutlined,
  PlusOutlined,
  MinusOutlined
} from '@ant-design/icons';
import { Can } from '@app/modules/Auth';
import { useStores } from '@app/hooks/stores';
import { Cron } from '@app/common/Cron';
import api from '../api';
import { CronProfile } from '@app/modules/Profiles/Schedule/store';
import { convertCronStructure } from '@app/utils/helpers';

declare type ProfileItemProps = {
  item: CronProfile;
};

const ProfileItem: React.FC<ProfileItemProps> = ({ item }) => {
  const { cronProfilesStore } = useStores();

  const { saveBackupItem, restoreBackedUpItem, removeCronProfile, listCronProfiles } = cronProfilesStore;

  const [deleteConfirmationsMap, setDeleteConfirmationVisibility] = useState<{ [key: number]: boolean }>({});
  const [editingItemsMap, setEditingMode] = useState<{ [key: number]: boolean }>({});

  const toggleEditingOnItem = (item, forcedValue) => {
    setEditingMode({
      ...editingItemsMap,
      ...{
        [item.id]: typeof forcedValue !== 'undefined' ? forcedValue : !editingItemsMap[item.id]
      }
    });
  };

  const onEditMode = (item: CronProfile) => {
    saveBackupItem(item);
    toggleEditingOnItem(item, true);
  };

  const onCancelEditing = (item: CronProfile) => {
    if (item.id) {
      restoreBackedUpItem(item);
    } else {
      removeCronProfile(item);
    }
    toggleEditingOnItem(item, false);
  };

  const onChangeProfileName = (item: CronProfile, event) => {
    const name = event.target.value;
    item.name = name;
  };

  const onSetCron = (index: number) => (cron: string) => {
    item.cron[index] = cron;
  };

  const onSave = async (item: CronProfile) => {
    const convertedCron = convertCronStructure(item.cron) as Array<string>;
    try {
      if (!item.name.trim()) {
        return notification.warning({ message: 'Please enter a name for the profile' });
      }
      const nameAlreadyExists = new Set(listCronProfiles.map(profile => profile.name));

      if (nameAlreadyExists.size < listCronProfiles.length)
        return notification.warning({ message: 'Profile with this name already exists' });
      const validCronStrings = convertedCron.every(cronString => !!cronString.trim());
      if (!validCronStrings) {
        return notification.warning({ message: 'Please fill cron params' });
      }
      if (item.id) {
        const { id, cron, name } = item;
        await api.editCronProfile(id, { cron: JSON.stringify(convertedCron), name });
        notification.success({
          message: 'Profile saved!'
        });
      } else {
        const id = await api.addCronProfile({ ...item, cron: JSON.stringify(convertedCron) });
        item.id = id;
        notification.success({
          message: 'Profile added successfully!'
        });
      }
    } catch (err) {
      console.log(err);
      notification.error({ message: 'Something went wrong', description: err.message });
    }

    toggleEditingOnItem(item, false);
  };

  const onProfileDelete = async (item: CronProfile) => {
    try {
      await api.deleteCronProfile(item.id);
      removeCronProfile(item);
      notification.success({
        message: `"${item.name}" deleted !`
      });
    } catch (e) {
      notification.error({
        message: 'Error',
        description: `Error when delete profile! ${e.message}`
      });
    }
  };

  const handleDeleteConfirmationVisibility = (visible, item) => {
    setDeleteConfirmationVisibility({
      ...deleteConfirmationsMap,
      ...{
        [item.id]: visible
      }
    });
  };

  const addOneMoreCronString = (item: CronProfile) => () => {
    const length = Object.keys(item.cron).length;
    if (length >= 10) {
      return notification.warning({ message: 'You can add only 10 cron strings' });
    }
    onEditMode(item);
    item.cron[length] = ' ';
  };

  const removeCronString = (index, item) => () => {
    onEditMode(item);
    delete item.cron[index];
  };

  const isEditing = !item.id || editingItemsMap[item.id];

  return (
    <List.Item
      key={item.id}
      actions={
        isEditing
          ? [
              <Can
                action="put:profiles/cron"
                yes={() => (
                  <Button type="link" icon={<CheckOutlined />} onClick={e => onSave(item)}>
                    Save
                  </Button>
                )}
                no={() => (
                  <Button type="link" icon={<CheckOutlined />} disabled>
                    Save
                  </Button>
                )}
              />,
              <Button type="link" icon={<StopOutlined />} onClick={e => onCancelEditing(item)}>
                Cancel
              </Button>
            ]
          : [
              <Can
                action="put:profiles/cron"
                yes={() => (
                  <Button type="link" icon={<EditOutlined />} onClick={e => onEditMode(item)}>
                    Edit
                  </Button>
                )}
                no={() => (
                  <Button type="link" disabled icon={<EditOutlined />}>
                    Edit
                  </Button>
                )}
              />,
              <Can
                action="delete:profiles/cron"
                yes={() => (
                  <Popconfirm
                    title={`Are you sure you want to delete "${item.name}" profile?`}
                    icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                    //@ts-ignore
                    open={deleteConfirmationsMap[item.id]}
                    onOpenChange={visible => handleDeleteConfirmationVisibility(visible, item)}
                    onConfirm={() => onProfileDelete(item)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button type="link" icon={<DeleteOutlined />}>
                      Delete
                    </Button>
                  </Popconfirm>
                )}
                no={() => (
                  <Button type="link" disabled icon={<DeleteOutlined />}>
                    Delete
                  </Button>
                )}
              />,
              <Can
                action="put:profiles/cron"
                yes={() => (
                  <Button type="link" icon={<PlusOutlined />} onClick={addOneMoreCronString(item)}>
                    Add one more cron string
                  </Button>
                )}
                no={() => (
                  <Button type="link" icon={<PlusOutlined />} disabled>
                    Add one more cron string
                  </Button>
                )}
              />
            ]
      }
    >
      <List.Item.Meta
        avatar={<ProfileOutlined />}
        title={
          isEditing ? (
            <Input
              value={item.name}
              size="small"
              onChange={value => onChangeProfileName(item, value)}
              autoFocus
              placeholder="Name"
            />
          ) : (
            <strong>{item.name}</strong>
          )
        }
        description={Object.keys(item?.cron)?.map(key => (
          <Row key={key} className="tenants-cron-row">
            <Button type="link" icon={<MinusOutlined />} onClick={removeCronString(key, item)}></Button>
            <Cron
              humanizeValue={false}
              disabled={!isEditing}
              readOnly={!isEditing}
              value={item.cron[key]}
              setValue={onSetCron(+key)}
              onError={console.warn}
              shortcuts={false}
              defaultPeriod="week"
              allowEmpty="never"
              className="tenants-cron"
            />
          </Row>
        ))}
      />
    </List.Item>
  );
};

export default observer(ProfileItem);
